import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';

import { parseApiError } from '@saas-kit/lib';
import { toast } from '@saas-kit/ui';

import {
  issueInvoice,
  markInvoicePaid,
  recordPayment,
  voidInvoice,
  voidPayment,
} from './api';
import type {
  AdminInvoice,
  AdminPayment,
  IssueInvoicePayload,
  MarkPaidPayload,
  RecordPaymentPayload,
  VoidInvoicePayload,
} from './types';

interface Args {
  tenantId: string;
  invoiceId: string;
}

export interface InvoiceMutations {
  issue: UseMutationResult<AdminInvoice, unknown,IssueInvoicePayload>;
  markPaid: UseMutationResult<AdminInvoice, unknown,MarkPaidPayload>;
  void: UseMutationResult<AdminInvoice, unknown,VoidInvoicePayload>;
  recordPayment: UseMutationResult<AdminPayment, unknown,RecordPaymentPayload>;
  voidPayment: UseMutationResult<AdminPayment, unknown,string>;
}

export function useInvoiceMutations({ tenantId, invoiceId }: Args): InvoiceMutations {
  const { t } = useTranslation('adminBilling');
  const queryClient = useQueryClient();

  async function invalidate(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['admin-billing-invoice', tenantId, invoiceId],
      }),
      queryClient.invalidateQueries({
        queryKey: ['admin-billing-invoices', tenantId],
      }),
    ]);
  }

  function onErr(err: unknown): void {
    toast.error(parseApiError(err).message);
  }

  const issue = useMutation({
    mutationFn: (payload: IssueInvoicePayload) =>
      issueInvoice(tenantId, invoiceId, payload),
    onSuccess: async () => {
      await invalidate();
      toast.success(t('toasts.issued'));
    },
    onError: onErr,
  });

  const markPaid = useMutation({
    mutationFn: (payload: MarkPaidPayload) =>
      markInvoicePaid(tenantId, invoiceId, payload),
    onSuccess: async () => {
      await invalidate();
      toast.success(t('toasts.marked_paid'));
    },
    onError: onErr,
  });

  const voidM = useMutation({
    mutationFn: (payload: VoidInvoicePayload) =>
      voidInvoice(tenantId, invoiceId, payload),
    onSuccess: async () => {
      await invalidate();
      toast.success(t('toasts.voided'));
    },
    onError: onErr,
  });

  const recordPaymentM = useMutation({
    mutationFn: (payload: RecordPaymentPayload) =>
      recordPayment(tenantId, invoiceId, payload),
    onSuccess: async () => {
      await invalidate();
      toast.success(t('toasts.payment_recorded'));
    },
    onError: onErr,
  });

  const voidPaymentM = useMutation({
    mutationFn: (paymentId: string) => voidPayment(tenantId, paymentId),
    onSuccess: async () => {
      await invalidate();
      toast.success(t('toasts.payment_voided'));
    },
    onError: onErr,
  });

  return {
    issue,
    markPaid,
    void: voidM,
    recordPayment: recordPaymentM,
    voidPayment: voidPaymentM,
  };
}
